
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import { AnimationShowcaseSection } from '@/components/landing/AnimateImage/AnimationShowcaseSection';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import MediaTemplatesSection from '@/components/media/MediaTemplatesSection';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import VideoModelsShowcase from '@/components/videos/VideoModelsShowcase';
import { HAS_FREE_PLAN } from '@/constants/common';
const AnimateImage = () => {
    const { t } = useLocale('ai-videos');
    const heroExample = {
        sourceImage: 'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/c320f772-98e0-4629-8dd4-f5f43fefa9af.png',
        animatedVideo: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/final_4646b29d-6523-42f4-8994-ef83a3000d3b.mp4',
    };
    const features = [
        {
            name: 'Prompt-Based Animation',
            description: 'Simply describe how you want your image to move and let AI do the magic',
            icon: '✨',
        },
        {
            name: 'Natural Movement',
            description: 'AI generates smooth, realistic motion that looks natural',
            icon: '🎭',
        },
        {
            name: 'Creative Control',
            description: 'Use text prompts to guide the animation exactly how you want it',
            icon: '🎨',
        },
        {
            name: 'High Quality Output',
            description: 'Get professional-grade animated videos in stunning quality',
            icon: '🎬',
        },
        {
            name: 'Easy to Use',
            description: 'No technical skills required - just upload, describe, and animate',
            icon: '🎯',
        },
        {
            name: 'Commercial Usage',
            description: 'Perfect for marketing, social media, and business content',
            icon: '💼',
        },
    ];
    return (<main className='relative bg-gray-100 dark:bg-slate-900'>
      <Seo templateTitle={t('AI Image Animator | Bring Your Photos to Life')} description={t('Transform static images into stunning animated videos with our AI technology. Create engaging content for social media, marketing, and more.')} image={`https://easy-peasy.ai/api/og-template?title=${t('AI Image Animator')}`}/>

      <LandingPageNavigation />

      {/* Hero Section */}
      <div className='relative isolate'>
        <div className='mx-auto max-w-7xl px-6 pb-24 pt-36 sm:pt-48 lg:px-8 lg:pt-32'>
          <div className='mx-auto flex flex-col items-center gap-16 lg:flex-row lg:items-center lg:gap-8'>
            <div className='lg:w-1/2'>
              <div className='text-center lg:text-left'>
                <h1 className='text-4xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-6xl'>
                  {t('Transform Static Images into')}{' '}
                  <span className='bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent'>
                    {t('Living Videos')}
                  </span>
                </h1>
                <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-300'>
                  {t('Bring your photos to life with AI-powered animation. Create captivating content by adding natural movement to any image - perfect for social media, marketing, and storytelling.')}
                </p>
                <div className='mt-10 flex items-center justify-center gap-x-6 lg:justify-start'>
                  <ButtonLink href={HAS_FREE_PLAN ? `/ai-videos` : '/#pricing'} variant='dark'>
                    {t('Start Animating Images')}
                  </ButtonLink>
                </div>
              </div>
            </div>
            <div className='lg:w-1/2'>
              <div className='flex items-center gap-4'>
                <div className='relative'>
                  <Image width={200} height={200} alt='Static Image' src={heroExample.sourceImage} className='w-[200px] rounded-lg shadow-lg'/>
                  <div className='absolute bottom-4 left-4 right-4 hidden rounded-lg bg-black/70 p-3 text-center text-sm text-white sm:block'>
                    {t('Original Static Image')}
                  </div>
                </div>
                <div className='flex items-center justify-center'>
                  <Image width={80} height={80} alt='arrow' className='w-[40px] rotate-0 transform sm:w-[80px]' src='/images/arrow.png'/>
                </div>
                <div className='relative'>
                  <video autoPlay loop muted playsInline className='w-[400px] rounded-lg shadow-lg' src={heroExample.animatedVideo}/>
                  <div className='absolute bottom-4 left-4 right-4 rounded-lg bg-black/70 p-3 text-center text-sm text-white'>
                    {t('AI-Generated Animation')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimationShowcaseSection />

      <MediaTemplatesSection kind='video' basePath='/ai-video-generator/templates' heading={t('AI Video Templates')} subheading={t('Browse high-intent video template pages, preview outputs, and start from proven prompts.')}/>

      {/* Models Showcase */}
      <div id='models'>
        <VideoModelsShowcase />
      </div>

      {/* How It Works Section */}
      <div className='mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8'>
        <div className='mx-auto max-w-2xl text-center'>
          <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            {t('How to Animate Your Images')}
          </h2>
          <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-300'>
            {t('Three simple steps to bring your images to life')}
          </p>
        </div>

        <div className='mx-auto mt-16 max-w-7xl'>
          <div className='grid grid-cols-1 gap-8 md:grid-cols-3'>
            <div className='text-center'>
              <div className='mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900'>
                <span className='text-2xl'>1</span>
              </div>
              <h3 className='mt-6 text-lg font-semibold'>{t('Upload')}</h3>
              <p className='mt-2 text-gray-600 dark:text-slate-300'>
                {t('Upload any image you want to bring to life')}
              </p>
            </div>

            <div className='text-center'>
              <div className='mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900'>
                <span className='text-2xl'>2</span>
              </div>
              <h3 className='mt-6 text-lg font-semibold'>{t('Describe')}</h3>
              <p className='mt-2 text-gray-600 dark:text-slate-300'>
                {t('Tell AI how you want your image to move and animate')}
              </p>
            </div>

            <div className='text-center'>
              <div className='mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900'>
                <span className='text-2xl'>3</span>
              </div>
              <h3 className='mt-6 text-lg font-semibold'>{t('Generate')}</h3>
              <p className='mt-2 text-gray-600 dark:text-slate-300'>
                {t('Get your AI-generated animated video in seconds')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Example Videos Grid */}
      {/* <div className='mx-auto max-w-7xl px-6 py-12 sm:py-16 lg:px-8'>
          <h2 className='text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            {t('See What You Can Create')}
          </h2>
          <div className='mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3'>
            {videoPrompts.map((video) => (
              <div key={video.id} className='relative overflow-hidden rounded-lg'>
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className='h-full w-full object-cover'
                  src={video.image_url}
                />
                <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black p-4'>
                  <p className='text-sm text-white'>{video.prompt}</p>
                </div>
              </div>
            ))}
          </div>
        </div> */}

      {/* Features Grid */}
      <div className='mx-auto max-w-7xl px-6 py-24  lg:px-8'>
        <div className='mx-auto max-w-2xl lg:text-center'>
          <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            {t('Everything You Need to Create Amazing Animations')}
          </h2>
        </div>
        <div className='mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none'>
          <dl className='grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3'>
            {features.map((feature) => (<div key={feature.name} className='flex flex-col'>
                <dt className='flex items-center gap-x-3 text-xl font-semibold leading-7 text-gray-900 dark:text-slate-200'>
                  <span className='text-2xl'>{feature.icon}</span>
                  {t(feature.name)}
                </dt>
                <dd className='mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600 dark:text-slate-300'>
                  <p className='flex-auto'>{t(feature.description)}</p>
                </dd>
              </div>))}
          </dl>
          <div className='mt-10 flex items-center justify-center gap-x-6'>
            <ButtonLink href={HAS_FREE_PLAN ? `/ai-videos` : '/#pricing'} variant='dark'>
              {t('Start Animating Images')}
            </ButtonLink>
          </div>
        </div>
      </div>

      <FeaturedLogos />
      <Testimonials />
      <CallToAction preset='ai-videos'/>
      <Footer />
    </main>);
};
export default AnimateImage;

    async function __Next_Translate__getStaticProps__1a167d9f24a__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-animate-image',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a167d9f24a__ as getStaticProps }
  